/**
 * Agent-API `POST /v1/transfer-statement/search` (shared ledger / audit)
 * → Transfer Statement table rows used by Money → Transfer Statement.
 *
 * Lab viewTransactionNew seat for the signed-in agent — not the analytics
 * downline-wide mart. Used when analytics is unavailable.
 */
import { unstopped, type Account, type Transfer } from '@/data/types'
import { STOP_COPY } from '@/lib/status'
import type { TransferMappedResult, TransferRowView } from './transfers'

export type LedgerTransferStatementRow = {
  id: string
  createdAt: string
  amount: number
  transactionType: 'Given' | 'Taken'
  fromUser: { id: string; username: string }
  toUser: { id: string; username: string }
  comments: string | null
  ip: string | null
}

export type LedgerTransferStatementPage = {
  results: LedgerTransferStatementRow[]
  total: number
  page: number
  size: number
  viewer: { id: string; username: string; currencyType: string }
}

function stubParty(id: string, username: string): Account {
  return {
    id,
    username: username || id.slice(0, 8) || '—',
    displayName: username || id.slice(0, 8) || '—',
    role: 'player',
    parentId: null,
    path: [],
    status: 'active',
    createdAt: '',
    lastActiveAt: null,
    creditLimit: 0,
    balance: 0,
    distributedBalance: 0,
    exposure: 0,
    settlement: 0,
    share: { sportsbook: 0, casino: 0 },
    commission: { fancy: 0, bookmaker: 0, matchOdds: 0 },
    betLimits: { min: 0, max: 0 },
    games: { casino: true, cardGame: true, liveGame: true, sportsbook: true },
    childCount: 0,
    memberCount: 0,
    flags: [],
    ...unstopped('active', STOP_COPY.notWiredLive),
  }
}

function toTransferRow(row: LedgerTransferStatementRow, selfId: string): TransferRowView {
  const direction: Transfer['direction'] =
    row.transactionType === 'Given' ? 'give' : 'take'
  const from = stubParty(row.fromUser.id, row.fromUser.username)
  const to = stubParty(row.toUser.id, row.toUser.username)
  return {
    id: row.id,
    ref: row.id,
    at: row.createdAt,
    fromId: row.fromUser.id,
    toId: row.toUser.id,
    amount: row.amount,
    direction,
    remark: row.comments?.trim() || '',
    balanceAfterFrom: 0,
    balanceAfterTo: 0,
    byId: selfId,
    from,
    to,
  }
}

/** Map Standard API envelope data → TransfersPage result (self-party only). */
export function ledgerTransferStatementToResult(
  page: LedgerTransferStatementPage,
  selfId: string,
): TransferMappedResult {
  const rows = (page.results ?? []).map((r) => toTransferRow(r, selfId))
  let given = 0
  let taken = 0
  for (const r of rows) {
    if (r.direction === 'give') given += r.amount
    else taken += r.amount
  }
  given = Math.round(given * 100) / 100
  taken = Math.round(taken * 100) / 100
  return {
    rows,
    total: typeof page.total === 'number' ? page.total : rows.length,
    balanceAfterIsLive: true,
    totals: {
      given,
      taken,
      net: Math.round((taken - given) * 100) / 100,
      count: typeof page.total === 'number' ? page.total : rows.length,
    },
    daily: [],
  }
}
